import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeftIcon, ChevronRightIcon } from 'lucide-react';
import type { Aop, Territory } from '../../types/dashboard';
import { useEntityData } from '../../hooks/useEntityData';
import { useMetricParam } from '../../hooks/useMetricParam';
import type { RetentionSummary } from '../../utils/retention';
import { monthLabel } from '../../utils/calendar';
import { fmtPct, fmtPP } from '../../utils/format';
import { formatChange, metricMeta, type TrendMetric } from '../../utils/metricConfig';
import { KpiCard } from '../ui/KpiCard';
import { Panel } from '../ui/Panel';
import { StatusBadge } from '../ui/StatusBadge';
import { TrendPanel } from '../charts/TrendPanel';
import { FunnelBars } from '../funnel/FunnelBars';
import { RetentionView } from '../retention/RetentionView';
import { BenchmarkTable } from './BenchmarkTable';
import { GapPanel } from './GapPanel';
import { AopTable } from './AopTable';
import { AopZipPanel } from './AopZipPanel';
function RetentionRow({
  label,
  r
}: {
  label: string;
  r: RetentionSummary;
}) {
  return <div className="flex items-baseline justify-between gap-3 border-b border-line py-2.5 last:border-0" data-id="element-605" data-mp="2gdskrzuu0t4w">
      <span className="text-xs text-ink-muted" data-id="element-606" data-mp="21ppeepcvnffk">{label}</span>
      <span className="text-right" data-id="element-607" data-mp="26nr0lj5lnuv9">
        <span className="text-sm font-semibold tabular-nums text-navy" data-id="element-608" data-mp="26osl0e8i9tpn">{r.rate === null ? 'No cohort' : fmtPct(r.rate)}</span>
        {r.rate !== null && r.network !== null && <span className={`ml-2 text-[11.5px] font-medium tabular-nums ${r.rate - r.network < 0 ? 'text-negative' : 'text-positive'}`} data-id="element-609" data-mp="2zcy6523h1kq0">{fmtPP(r.rate - r.network)}</span>}
        {r.network !== null && <span className="block text-[11px] text-ink-subtle" data-id="element-610" data-mp="2pdpeifw2wi6t">Network {fmtPct(r.network)}</span>}
      </span>
    </div>;
}
export function EntityDashboard({
  territory,
  aop
}: {
  territory: Territory;
  aop?: Aop;
}) {
  const [metric, setMetric] = useMetricParam();
  const {
    search
  } = useLocation();
  const isRetention = metric === 'retention';
  const trendMetric: TrendMetric = isRetention ? 'members' : metric;
  const d = useEntityData(territory, aop, trendMetric);
  const unitLabel = aop ? 'AOP' : 'territory';
  const entityName = aop ? aop.name : territory.name;
  const meta = metricMeta(trendMetric);
  return <div className="space-y-5" data-id="element-611" data-mp="31w1kr6aevgsr">
      <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-612" data-mp="31uqhd7kvob26">
        <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1 text-xs" data-id="element-613" data-mp="2wzb7eqpzqzb1">
          <Link to={`/${search}`} className="font-medium text-ink-muted hover:text-navy hover:underline" data-id="element-614" data-mp="2svd92i8hpsj1">Network</Link>
          <ChevronRightIcon className="h-3.5 w-3.5 text-ink-subtle" data-id="element-615" data-mp="2nxbnnqnldh4e" />
          {aop ? <Link to={`/territory/${territory.id}${search}`} className="font-medium text-ink-muted hover:text-navy hover:underline" data-id="element-616" data-mp="2izjocbkmasq6">{territory.name}</Link> : <span className="font-semibold text-navy" aria-current="page" data-id="element-617" data-mp="h7nb8xisxata">{territory.name}</span>}
          <ChevronRightIcon className="h-3.5 w-3.5 text-ink-subtle" data-id="element-618" data-mp="op39oz9nw34j" />
          {aop ? <span className="font-semibold text-navy" aria-current="page" data-id="element-619" data-mp="topmx8phpjdc">{aop.name}</span> : <span className="text-ink-subtle" data-id="element-620" data-mp="yfy2ynlbs2zh">Select an AOP below</span>}
        </nav>
        <Link to={`/${search}`} className="inline-flex h-8 items-center gap-1.5 rounded-md border border-line bg-white px-3 text-xs font-semibold text-navy transition-colors duration-150 hover:bg-navy-50" data-id="element-621" data-mp="yiag8ng51p67">
          <ArrowLeftIcon className="h-3.5 w-3.5" data-id="element-622" data-mp="apk52gg279wz" />
          Back to dashboard
        </Link>
      </div>

      <div className="flex flex-wrap items-end justify-between gap-4 rounded-lg border border-line bg-white px-5 py-4" data-id="element-623" data-mp="fp6rnghs22ig">
        <div data-id="element-624" data-mp="kpb3uubnkb2v">
          <div className="flex flex-wrap items-center gap-2.5" data-id="element-625" data-mp="kpule9xe4xgd">
            <h2 className="text-xl font-bold tracking-tight text-navy" data-id="element-626" data-mp="1r3k29jnislgj">{entityName}</h2>
            {!isRetention && <StatusBadge status={d.status} data-id="element-627" data-mp="1w2nafcysr7sq" />}
          </div>
          <p className="mt-1 text-[13px] text-ink-muted" data-id="element-628" data-mp="1tp1hec7wsly8">
            {aop ? `${aop.venue} · ${aop.zipCount} ZIP codes · ${territory.name}, ${territory.state}` : `${territory.state} · ${territory.aops.length} AOPs`}
            {' · '}FBC {territory.fbc} · AD {territory.ad ?? '—'}
            {territory.launchMonth > 0 && ` · launched ${monthLabel(territory.launchMonth)}`}
          </p>
        </div>
        {!isRetention && <p className="text-xs text-ink-muted" data-id="element-629" data-mp="2cmucwtg7dtll">
            {d.range.label} · {meta.label.toLowerCase()}{' '}
            <b className={d.gap < 0 ? 'text-negative' : 'text-positive'} data-id="element-630" data-mp="28liwnej7ykq2">{formatChange(trendMetric, d.gap)}</b> vs network avg per {unitLabel}
          </p>}
      </div>

      {isRetention ? <>
          <RetentionView territoryIds={[territory.id]} aopIds={aop ? [aop.id] : []} entityLabel={entityName} data-id="element-631" data-mp="2hfe1smkccb5p" />
          <p className="text-xs text-ink-muted" data-id="element-632" data-mp="2w9sd00xnyg9t">
            Switch to another metric above to see {entityName}’s acquisition trends and AOP comparison.{' '}
            <button type="button" onClick={() => setMetric('members')} className="font-semibold text-navy hover:underline" data-id="element-633" data-mp="3azmyva95th88">Show members</button>
          </p>
        </> : <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5" data-id="element-634" data-mp="31175f0mntvvk">
            {d.kpis.map(k => <KpiCard key={k.id} item={k} data-id="element-635" data-mp="35y7dfh32l8pb" />)}
          </div>

          <div className="grid gap-5 xl:grid-cols-12" data-id="element-636" data-mp="2ra2wahjo158n">
            <div className="min-w-0 space-y-5 xl:col-span-8" data-id="element-637" data-mp="2r8hymj22166k">
              <TrendPanel metric={trendMetric} series={d.series} network={d.network} range={d.range} scopeUnits={d.scopeUnits} networkUnits={d.networkUnits} unitLabel={aop ? 'AOP' : 'territory'} entityLabel={entityName} showScopeAvg data-id="element-638" data-mp="2w24d8grs3k50" />
              <AopTable territory={territory} rows={d.aopRows} metric={trendMetric} selectedAopId={aop?.id} networkAvg={d.aopBenchValue} territoryAvg={d.territoryAvgValue} data-id="element-639" data-mp="2d9s2b5n27aj9" />
              <Panel title="Comparison to network" definitionId="networkAverage" subtitle={`${entityName} vs network average per active ${unitLabel} · all metrics · same filters and range`} data-id="element-640" data-mp="1kfvgvaj1fas6">
                <BenchmarkTable items={d.benchmarkItems} entityLabel={aop ? 'This AOP' : 'This territory'} benchLabel={`Network avg / ${unitLabel}`} data-id="element-641" data-mp="2d5m1m2f6dax9" />
              </Panel>
            </div>

            <div className="min-w-0 space-y-5 xl:col-span-4" data-id="element-642" data-mp="236mp28ragv4z">
              <GapPanel items={d.benchmarkItems} unitLabel={unitLabel} data-id="element-643" data-mp="2351x8qwczqat" />
              <Panel title="Members to registrations" definitionId="conversion" subtitle={`${d.range.label} · same-period`} data-id="element-644" data-mp="1y70btzbgnew6">
                <FunnelBars compact members={d.metrics.members} registeringMembers={d.metrics.registeringMembers} registrations={d.metrics.registrations} conversionRate={d.metrics.conversionRate} regsPerMember={d.metrics.regsPerMember} data-id="element-645" data-mp="rt0z4ldlupie" />
              </Panel>
              <Panel title="Retention" definitionId="seasonRetention" subtitle="Unique participants · program season" actions={<button type="button" onClick={() => setMetric('retention')} className="text-xs font-semibold text-navy hover:underline" data-id="element-647" data-mp="3i37kqjd5sl4x">
                    Open retention
                  </button>} data-id="element-646" data-mp="38cl9108kceri">
                <RetentionRow label={`Season: ${d.retention.season.label}`} r={d.retention.season} data-id="element-648" data-mp="3r2ukz1u5jkwe" />
                <RetentionRow label={`YOY: ${d.retention.yoy.label}`} r={d.retention.yoy} data-id="element-649" data-mp="3w1e6x28qwwor" />
              </Panel>
              <AopZipPanel territory={territory} selectedAop={aop} data-id="element-650" data-mp="21ppm4jfu1zwg" />
            </div>
          </div>
        </>}
    </div>;
}